import { connectionConfig } from '@/config/chart';
import { logToServer } from '@/services/LogService';
import { store } from '@/state/store';
import { ResolutionString } from '@/types/charting_library';
import { Logger } from '@/utils/logger';
import BigNumber from 'bignumber.js';

const requests: Record<string, any> = {};
const inflight = new Map<string, Promise<any>>();
const NDConnection = new global.NetDania.JsApi.JSONConnection(connectionConfig);

let retriesOnCode128 = 0;

// ------------------------
// Error Handling
// ------------------------
function handleError(err: any) {
    const request = requests[err.id];
    const resolve = request?.resolve;
    const timeout = request?.timeout;

    const clearTimeoutHandler = () => {
        if (timeout) {
            clearTimeout(timeout);
            // Logger({
            //     title: `Chart timeout cleared in error handler - ${request?.chartKey}`,
            //     callback: () => console.log({})
            // });
        }
    };

    switch (err.code) {
        case -128: // Instrument not available
            Logger({
                title: `Inbound: Chart fetch error, instrument not available - ${request?.chartKey}`,
                callback: () => console.log({ err, request, retriesOnCode128 })
            });
            clearTimeoutHandler();
            resolve?.([]);
            break;
        case -127: // Instrument not allowed
            Logger({
                title: `Inbound: Chart fetch error, instrument not allowed - ${request?.chartKey}`,
                callback: () => console.log({ err, request })
            });
            clearTimeoutHandler();
            resolve?.([]);
            break;
        default:
            Logger({
                title: `Inbound: Chart fetch unknown error - code: ${err.code} - ${request?.chartKey}`,
                callback: () => console.log({ err, request })
            });
            clearTimeoutHandler();
            resolve?.([], { noData: true });
            break;
    }
}

// ------------------------
// Format NetDania results for TradingView
// ------------------------
const formatResults = (results: any) => {
    return results[0].map((_, i) => ({
        time: results[0][i] * 1000,
        open: results[1][i],
        high: results[2][i],
        low: results[3][i],
        close: results[4][i],
        volume: results[5][i]
    }));
};

// ------------------------
// Process incoming historical data
// ------------------------
function handleData(monitorChartResponse: any) {
    const TimeSeries = monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_TIME_STAMP);
    const OpenSeries =
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_OPEN) ||
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_CLOSE);
    const HighSeries =
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_HIGH) ||
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_CLOSE);
    const LowSeries =
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_LOW) ||
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_CLOSE);
    const CloseSeries = monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_CLOSE);
    const VolumeSeries =
        monitorChartResponse.get(global.NetDania.JsApi.Fields.CHART_VOLUME) || new Array(TimeSeries.length).fill(0);

    const chartData = [TimeSeries, OpenSeries, HighSeries, LowSeries, CloseSeries, VolumeSeries];
    const request = requests[monitorChartResponse.id];

    Logger({
        title: `Inbound: Resolving chart data - ${request?.chartKey}`,
        callback: () =>
            console.log({
                chartDataLength: TimeSeries.length,
                requests,
                monitorChartResponse
            })
    });

    const credentials = store.getState().auth.user;
    if (credentials?.username) {
        logToServer(
            'info',
            `User [${credentials.username}] received chart data. ${JSON.stringify({
                symbol: request?.req?.s,
                chartDataLength: TimeSeries.length
            })}`
        );
    }

    if (request?.timeout) {
        clearTimeout(requests[monitorChartResponse.id].timeout);
        // Logger({
        //     title: `Chart timeout cleared, resolved promise`,
        //     callback: () => console.log({})
        // });
    }
    request?.resolve(chartData);
}

// ------------------------
// Event Listeners
// ------------------------
NDConnection.addListener(global.NetDania.JsApi.Events.ONHISTORICALDATA, handleData);
NDConnection.addListener(global.NetDania.JsApi.Events.ONHISTORICALCHARTDATA, handleData);

NDConnection.addListener(global.NetDania.JsApi.Events.ONERROR, (err) => {
    const request = requests[err.id];
    const originalRequest = requests[err.id]?.req;

    if (err.code === -128 && originalRequest) {
        if (retriesOnCode128 < 2) {
            retriesOnCode128 += 1;
            const points = Math.max(originalRequest.pt, 30);

            const retryReq = global.NetDania.JsApi.Request.getReqChartHistory(
                originalRequest.s,
                originalRequest.ts,
                points,
                'netdania_rtc',
                false,
                originalRequest.sd,
                originalRequest.ed
            );

            requests[retryReq.i] = {
                id: request.id,
                resolve: request.resolve,
                chartKey: request.chartKey,
                timeout: request.timeout,
                req: retryReq
            };
            Logger({
                title: `Outbound: Retry fetch on netdania_rtc - ${request?.chartKey}`,
                callback: () => console.log({ retryReq })
            });

            NDConnection.AddRequest(retryReq);
            setTimeout(() => NDConnection.Flush(), 10);
            return;
        } else if (retriesOnCode128 < 5) {
            retriesOnCode128 += 1;
            const extendedReq = global.NetDania.JsApi.Request.getReqChartHistoryExtended(
                originalRequest.s,
                originalRequest.ts,
                originalRequest.pt,
                'netdania_rtc',
                originalRequest.ed,
                false,
                false
            );

            requests[extendedReq.i] = {
                id: request.id,
                resolve: request.resolve,
                chartKey: request.chartKey,
                timeout: request.timeout,
                req: extendedReq
            };
            Logger({
                title: `Outbound: Extended fetch on netdania_rtc - ${request?.chartKey}`,
                callback: () => console.log({ extendedReq })
            });

            NDConnection.AddRequest(extendedReq);
            setTimeout(() => NDConnection.Flush(), 10);
            return;
        }
    }

    handleError(err);
});

// ------------------------
// Utility: In-flight deduplication key
// ------------------------
function makeKey(symbol: string, interval: number, from: number, to: number, chartId: string) {
    return `${symbol}|${interval}|${from}|${to}|${chartId}`;
}

// ------------------------
// Unified fetch function
// ------------------------
export const fetchHistory = (
    id: string,
    symbol: string,
    interval: number,
    periodParams: { from: number; to: number; firstDataRequest?: boolean },
    provider: string,
    onHistoryCallback: (bars: any[], meta: { noData: boolean }) => void,
    onErrorCallback: (err: any) => void
) => {
    const { from, to, firstDataRequest } = periodParams;

    const MAX_INITIAL_BARS = 100;
    const MAX_BACKFILL_BARS = 150;

    const maxBars = firstDataRequest ? MAX_INITIAL_BARS : MAX_BACKFILL_BARS;

    /**
     * 🚨 NetDania REQUIRES from + to
     */
    const resolutionInSeconds = interval * 60;

    const requestTo = to || Math.floor(Date.now() / 1000);
    if (requestTo <= 0) {
        onHistoryCallback([], { noData: true });
        return Promise.resolve([]);
    }

    const requestFrom = requestTo - maxBars * resolutionInSeconds;
    // const requestFrom = firstDataRequest ? requestTo - maxBars * resolutionInSeconds : from;

    const key = makeKey(symbol, interval, 0, requestTo, id);
    if (inflight.has(key)) return inflight.get(key);

    const promise = new Promise<any[]>((resolve, reject) => {
        const chartKey = id.slice(-4);
        try {
            const req = global.NetDania.JsApi.Request.getReqChartHistory(
                symbol,
                interval,
                maxBars,
                provider,
                false,
                0,
                requestTo
            );

            const timeout = setTimeout(() => {
                const credentials = store.getState().auth.user;

                logToServer(
                    'warn',
                    `User [${
                        credentials?.username
                    }] experiencing slow chart loading, potentially stale chart. Request details: ${JSON.stringify({
                        symbol,
                        interval,
                        maxBars,
                        requestFrom: 0,
                        requestTo,
                        firstDataRequest
                    })}`
                );
                NDConnection.Flush();
                // Logger({
                //     title: `Unresolved chart fetch timeout - ${chartKey}`,
                //     callback: () =>
                //         console.log({
                //             symbol,
                //             interval,
                //             maxBars,
                //             requestFrom: 0,
                //             requestTo,
                //             firstDataRequest
                //         })
                // });
            }, 10_000); // 10 second timeout

            requests[req.i] = { req, resolve, id, chartKey, timeout };

            Logger({
                title: `Outbound: Chart fetch - ${chartKey}`,
                callback: () =>
                    console.log({
                        symbol,
                        interval,
                        maxBars,
                        requestFrom: 0,
                        requestTo,
                        firstDataRequest
                    })
            });

            NDConnection.AddRequest(req);
            // setTimeout(() => NDConnection.Connect(), 10);
            setTimeout(() => NDConnection.Flush(), 10);
        } catch (err) {
            Logger({
                title: `Outbound: Chart fetch error - ${chartKey}`,
                callback: () =>
                    console.log({
                        symbol,
                        interval,
                        maxBars,
                        requestFrom: 0,
                        requestTo,
                        firstDataRequest
                    })
            });

            reject(err);
        }
    });

    inflight.set(key, promise);

    promise
        .then((results: any[]) => {
            if (!results || results.length === 0) {
                onHistoryCallback([], { noData: true });
                return;
            }

            const bars = formatResults(results);

            /**
             * Stop backfill when NetDania returns fewer bars than requested
             */
            // const noData = !firstDataRequest && bars.length < maxBars;

            onHistoryCallback(bars, { noData: false });
        })
        .catch(onErrorCallback)
        .finally(() => inflight.delete(key));

    return promise;
};

// ------------------------
// Chart Utilities
// ------------------------
export const minInterval = (interval: number) => {
    if (interval === 604_800) return 2_592_000;
    if (interval === 2_592_000) return 31_536_000;
    return 172_800;
};

export const convertResolutionToSeconds = (resolution: ResolutionString) => {
    switch (resolution) {
        case '1T':
            return 0;
        case '1S':
            return 1;
        case '1':
            return 60;
        case '15':
            return 900;
        case '60':
        case '1H':
            return 3600;
        case '240':
        case '4H':
            return 14400;
        case '720':
        case '12H':
            return 43200;
        case '1D':
            return 86400;
        case '1W':
            return 604800;
        case '1M':
            return 2592000;
        default:
            return 86400;
    }
};

export const constructSymbolInfo = (symbolItem: any, configurationData: any) => {
    return {
        ticker: symbolItem.full_name,
        name: symbolItem.symbol,
        description: symbolItem.description,
        celer: symbolItem.symbol,
        type: symbolItem.type,
        session: '24x7',
        timezone: 'Etc/UTC',
        exchange: symbolItem.exchange,
        minmov: BigNumber(10)
            .pow(-symbolItem?.instrument?.pip_size || 1)
            .toNumber(),
        pricescale: 100,
        visible_plots_set: 'ohlc',
        supported_resolutions: configurationData.supported_resolutions as any,
        volume_precision: 2,
        data_status: 'streaming',
        has_ticks: true,
        has_seconds: true,
        has_intraday: true,
        has_weekly_and_monthly: true,
        seconds_multipliers: ['1'],
        weekly_multipliers: ['1'],
        monthly_multipliers: ['1']
    };
};
